import { useEffect, useState } from "react";

export enum Alignments {
  LEFT,
  RIGHT,
}

type Args = {
  pointAtRef: React.RefObject<HTMLElement | null>;
  alignment: Alignments;
  offsetTopPx: number;

  containerRef?: React.RefObject<HTMLElement | null>;
};

type Position = {
  position: "fixed" | "absolute";
  top: string;
  right: string;
  left: string;
  bottom: string;
};

/**
 * Calculates the coordinates required to positioned something near a given
 * target based on the passed alignment.
 *
 * If no containerRef is passed, then it is assumed that fixed positioning
 * should be used and that the element using these coordinates is mounted at
 * the root of the page.
 *
 * NB: Will only position the target below the pointAt element.
 */
export function usePositionAt({
  pointAtRef,
  containerRef,
  alignment,
  offsetTopPx,
}: Args): Position {
  const [position, setPosition] = useState<Position>({
    position: "fixed",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
  });

  useEffect(() => {
    const pointAt = pointAtRef.current;
    const container = containerRef?.current;

    if (!pointAt) {
      return;
    }

    const pointAtPos = pointAt.getBoundingClientRect();

    switch (alignment) {
      case Alignments.RIGHT:
        if (container) {
          const offset = getAbsolutePositionWithinContainer(container, pointAt);

          setPosition({
            position: "absolute",
            top: `${offset.top + pointAt.clientHeight + offsetTopPx}px`,
            right: `${
              container.clientWidth - (offset.left + pointAt.clientWidth)
            }px`,
            left: "auto",
            bottom: "auto",
          });
        } else {
          setPosition({
            position: "fixed",
            top: `${pointAtPos.bottom + offsetTopPx}px`,
            right: `${window.innerWidth - pointAtPos.right}px`,
            left: "auto",
            bottom: "auto",
          });
        }
        break;

      case Alignments.LEFT:
        if (container) {
          const offset = getAbsolutePositionWithinContainer(container, pointAt);

          setPosition({
            position: "absolute",
            top: `${offset.top + pointAt.clientHeight + offsetTopPx}px`,
            left: `${offset.left}px`,
            right: "auto",
            bottom: "auto",
          });
        } else {
          setPosition({
            position: "fixed",
            top: `${pointAtPos.bottom + offsetTopPx}px`,
            left: `${pointAtPos.left}px`,
            right: "auto",
            bottom: "auto",
          });
        }

        break;
    }
  }, [pointAtRef, containerRef, alignment, offsetTopPx]);

  return position;
}

function getAbsolutePositionWithinContainer(
  container: HTMLElement,
  target: HTMLElement
): { top: number; left: number } {
  const pos = {
    left: 0,
    top: 0,
  };

  let currentElement: HTMLElement | null = target;

  while (currentElement !== container && currentElement !== null) {
    pos.left += currentElement.offsetLeft;
    pos.top += currentElement.offsetTop;

    currentElement = currentElement.parentElement;
  }

  return pos;
}
